import React, { useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import { zodResolver } from "@hookform/resolvers/zod";
import { ConfirmationModal, FormModal, FormGroupContainer } from "@packsize/components";
import { Grid, TextField, Typography } from "@mui/material";

import { CopyComponent } from "@/components/solution-preview/copy-component";
import {
	getProductionGroupConfigurationSchema,
	type ProductionGroupConfiguration,
	type Corrugate,
	type MachineGroup,
} from "@/schemas";
import type { ProductionGroup } from "@/schemas/production-group";

import ManualScanToProduceDropDown, {
	isScanToTriggerConfigured,
	type ScanToProduceOption,
} from "../manual-scan-to-produce-dropdown";
import type { ProductionGroupLookup } from "../types";

import { MachineGroupsSelectionList } from "./components/machine-groups-selection-list";
import { type AccordionPanel, SelectionPipelineConfiguration } from "./components/selection-pipeline-configuration";
import { ZFoldSelectionList } from "./components/z-fold-selection-list";
import { filterNames } from "./pipeline-steps";

export interface ProductionGroupsConfigurationModalTemplateProps {
	readonly open: boolean;
	readonly productionGroup?: ProductionGroup;
	readonly defaultValues: ProductionGroupConfiguration;
	readonly machineGroups: MachineGroup[];
	readonly corrugates: Corrugate[];
	readonly machineGroupSelectable: Record<string, boolean>;
	readonly machineGroupAssignedTo: ProductionGroupLookup;
	readonly errorMessage?: string;
	readonly isSaving: boolean;
	readonly onSubmit: (data: ProductionGroupConfiguration) => Promise<void>;
	readonly onClose: () => void;
}

export const ProductionGroupsConfigurationModalTemplate: React.FC<ProductionGroupsConfigurationModalTemplateProps> = ({
	open,
	productionGroup,
	defaultValues,
	machineGroups,
	corrugates,
	machineGroupSelectable,
	machineGroupAssignedTo,
	errorMessage,
	isSaving,
	onSubmit,
	onClose,
}) => {
	const intl = useIntl();
	const [expandedPanel, setExpandedPanel] = useState<AccordionPanel | false>("filters");
	const [confirmLeaveOpen, setConfirmLeaveOpen] = useState(false);
	const wasOpen = useRef(false);

	const {
		register,
		control,
		handleSubmit,
		reset,
		setValue,
		getValues,
		formState: { errors, isDirty, isSubmitting },
	} = useForm<ProductionGroupConfiguration>({
		resolver: zodResolver(getProductionGroupConfigurationSchema(intl)),
		defaultValues,
	});

	useEffect(() => {
		if (open && !wasOpen.current) {
			reset(defaultValues);
			setExpandedPanel("filters");
		}
		wasOpen.current = open;
	}, [open, defaultValues, reset]);

	const handleAccordionChange =
		(panel: AccordionPanel) =>
		(_event: React.SyntheticEvent, isExpanded: boolean): void => {
			setExpandedPanel(isExpanded ? panel : false);
		};

	const handleRequestClose = (): void => {
		if (isDirty) {
			setConfirmLeaveOpen(true);
			return;
		}

		onClose();
	};

	return (
		<>
			<ConfirmationModal
				open={confirmLeaveOpen}
				onClose={() => setConfirmLeaveOpen(false)}
				onCancel={() => setConfirmLeaveOpen(false)}
				onConfirm={() => {
					setConfirmLeaveOpen(false);
					onClose();
				}}
				title={intl.formatMessage({ id: "Leave without saving?", defaultMessage: "Leave without saving?" })}
				confirmText={intl.formatMessage({ id: "Leave", defaultMessage: "Leave" })}
				cancelText={intl.formatMessage({ id: "Cancel", defaultMessage: "Cancel" })}
				size="sm"
				slotProps={{
					confirmButton: {
						"data-testid": "production-groups-configuration-modal__button__confirm-leave",
						"data-pendoid": "production-groups-configuration-modal__button__confirm-leave",
					},
					cancelButton: {
						"data-testid": "production-groups-configuration-modal__button__cancel-leave",
						"data-pendoid": "production-groups-configuration-modal__button__cancel-leave",
					},
				}}
			>
				<div>
					{intl.formatMessage({ id: "All unsaved work will be lost", defaultMessage: "All unsaved work will be lost" })}
				</div>
			</ConfirmationModal>
			<FormModal
				data-cy="add-form"
				open={open}
				title={
					productionGroup
						? intl.formatMessage({ id: "Edit Production Group", defaultMessage: "Edit Production Group" })
						: intl.formatMessage({ id: "New Production Group", defaultMessage: "New Production Group" })
				}
				onSubmit={handleSubmit(onSubmit)}
				sx={{ minWidth: "95vw", height: "98vh" }}
				submitText={intl.formatMessage({ id: "Save", defaultMessage: "Save" })}
				cancelText={intl.formatMessage({ id: "Cancel", defaultMessage: "Cancel" })}
				onClose={handleRequestClose}
				onCancel={handleRequestClose}
				slotProps={{
					modalContainer: {
						sx: { height: "100%" },
					},
					confirmButton: {
						loading: isSubmitting || isSaving,
						"data-testid": "production-groups-configuration-modal__button__confirm",
						"data-pendoid": "production-groups-configuration-modal__button__confirm",
					},
					cancelButton: {
						"data-testid": "production-groups-configuration-modal__button__cancel",
						"data-pendoid": "production-groups-configuration-modal__button__cancel",
					},
				}}
			>
				<Grid container spacing={4} sx={{ height: "100%" }}>
					<Grid size={{ xs: 12, md: 3 }} sx={{ height: "100%" }}>
						<TextField
							sx={{ mt: 1.3, mb: 3 }}
							label={intl.formatMessage({ id: "Name", defaultMessage: "Name" })}
							fullWidth
							error={Boolean(errors?.alias)}
							helperText={errors?.alias?.message}
							{...register("alias")}
							slotProps={{
								htmlInput: {
									"data-testid": "production-groups-configuration-modal__input__alias",
									"data-pendoid": "production-groups-configuration-modal__input__alias",
								},
								formHelperText: { "data-testid": "production-groups-configuration-modal__input__alias-error" },
							}}
						/>
						<TextField
							label={intl.formatMessage({ id: "Description", defaultMessage: "Description" })}
							fullWidth
							multiline
							rows={4}
							sx={{ mb: 3 }}
							error={Boolean(errors?.description)}
							helperText={errors?.description?.message}
							{...register("description")}
							slotProps={{
								htmlInput: {
									"data-testid": "production-groups-configuration-modal__input__description",
									"data-pendoid": "production-groups-configuration-modal__input__description",
								},
								formHelperText: { "data-testid": "production-groups-configuration-modal__input__description-error" },
							}}
						/>
						{Boolean(productionGroup?.id) === true && (
							<TextField
								label={intl.formatMessage({ id: "Production Group ID", defaultMessage: "Production Group ID" })}
								fullWidth
								sx={{ mb: 3, "& .MuiInputBase-input": { fontSize: "0.90rem" } }}
								value={productionGroup?.id ?? ""}
								slotProps={{
									input: {
										readOnly: true,
										endAdornment: <CopyComponent data={productionGroup?.id} />,
									},
									htmlInput: {
										"data-testid": "production-groups-configuration-modal__input__id",
										"data-pendoid": "production-groups-configuration-modal__input__id",
									},
								}}
							/>
						)}
						<Controller
							name="manualScanToProduce"
							control={control}
							render={({ field }) => (
								<ManualScanToProduceDropDown
									selectedOption={field.value as ScanToProduceOption}
									handleChange={(value: ScanToProduceOption) => {
										const pipeline = getValues("pipeline");
										if (
											isScanToTriggerConfigured(value) &&
											!pipeline.filterSteps.some((step) => step.name === filterNames.byReleasedForProductionDate)
										) {
											setValue("pipeline", {
												...pipeline,
												filterSteps: [
													...pipeline.filterSteps,
													{ name: filterNames.byReleasedForProductionDate, configuration: null },
												],
											});
										}

										field.onChange(value);
									}}
								/>
							)}
						/>
					</Grid>
					<Grid size={{ xs: 12, md: 4 }} sx={{ display: "flex", flexDirection: "column", gap: 2, height: "100%" }}>
						<FormGroupContainer
							legend={intl.formatMessage({ id: "z-Folds", defaultMessage: "z-Folds" })}
							sx={{
								m: 0,
								flex: 1,
								minHeight: 0,
								display: "flex",
								flexDirection: "column",
							}}
						>
							<Controller
								name="configuredCorrugates"
								control={control}
								render={({ field }) => (
									<ZFoldSelectionList corrugates={corrugates} selectedIds={field.value} onChange={field.onChange} />
								)}
							/>
						</FormGroupContainer>
						<FormGroupContainer
							sx={{
								flex: 1,
								minHeight: 0,
								display: "flex",
								flexDirection: "column",
							}}
							legend={intl.formatMessage({ id: "Machine Groups", defaultMessage: "Machine Groups" })}
						>
							<Controller
								name="configuredMachineGroups"
								control={control}
								render={({ field }) => (
									<MachineGroupsSelectionList
										machineGroups={machineGroups}
										selectedIds={field.value}
										machineGroupSelectable={machineGroupSelectable}
										machineGroupAssignedTo={machineGroupAssignedTo}
										onChange={field.onChange}
									/>
								)}
							/>
						</FormGroupContainer>
					</Grid>
					<Grid size={{ xs: 12, md: 5 }} sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
						<Typography component="div" sx={{ fontSize: "0.9rem", mb: 1 }}>
							<FormattedMessage id="Selection Pipeline" defaultMessage="Selection Pipeline" />
						</Typography>
						<Controller
							name="pipeline"
							control={control}
							render={({ field }) => (
								<SelectionPipelineConfiguration
									pipeline={field.value}
									onChange={field.onChange}
									expandedPanel={expandedPanel}
									onAccordionChange={handleAccordionChange}
								/>
							)}
						/>
					</Grid>

					{Boolean(errorMessage) === true && (
						<Grid size={12}>
							<Typography
								color="error"
								whiteSpace="pre-wrap"
								data-testid="production-groups-configuration-modal__save__error"
							>
								{errorMessage}
							</Typography>
						</Grid>
					)}
				</Grid>
			</FormModal>
		</>
	);
};
